import { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, CheckCircle, XCircle, RefreshCw, Eye, Key, Upload, Settings, Play } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingUser, setUploadingUser] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [showDataGenModal, setShowDataGenModal] = useState(false);
  const [dataGenUser, setDataGenUser] = useState(null);
  const [dataGenConfig, setDataGenConfig] = useState({
    enabled: false,
    schedule_type: 'daily',
    schedule_time: '00:00',
    schedule_day: 'monday'
  });
  const [historicalPatterns, setHistoricalPatterns] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadId, setUploadId] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone_number: '',
    is_admin: false,
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      // Ensure we always set an array
      const usersData = Array.isArray(response.data) ? response.data : [];
      console.log('Fetched users:', usersData.length, 'users');
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]); // Set empty array on error
      // Don't show toast error here - handle it in callers if needed
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.put(`/admin/users/${editingUser.id}`, formData);
        toast.success('User updated successfully');
      } else {
        await api.post('/admin/users', formData);
        toast.success('User created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handlePasswordReset = async (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setShowPasswordReset(true);
  };

  const confirmPasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    try {
      const response = await api.post(`/admin/users/${resetPasswordUser.id}/reset-password`, {
        new_password: newPassword
      });
      setShowPasswordReset(false);
      setNewPassword('');
      setResetPasswordUser(null);
      toast.success(response.data.message || 'Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    }
  };

  const handleOpenDataGenConfig = async (user) => {
    setDataGenUser(user);
    try {
      const [configResponse, patternsResponse] = await Promise.all([
        api.get(`/admin/users/${user.id}/data-generation/config`),
        api.get(`/admin/users/${user.id}/data-generation/patterns`)
      ]);
      setDataGenConfig(configResponse.data);
      setHistoricalPatterns(patternsResponse.data.patterns || null);
      setShowDataGenModal(true);
    } catch (error) {
      toast.error('Failed to load configuration');
    }
  };

  const handleSaveDataGenConfig = async () => {
    try {
      await api.post(`/admin/users/${dataGenUser.id}/data-generation/configure`, dataGenConfig);
      toast.success('Data generation configured successfully!');
      setShowDataGenModal(false);
      fetchUsers(); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    }
  };

  const handleRunDataGenNow = async (user) => {
    if (!window.confirm(`Generate data for ${user.first_name} ${user.last_name} now?`)) return;
    
    let loadingToast;
    try {
      loadingToast = toast.loading('Generating data...');
      const response = await api.post(`/admin/users/${user.id}/data-generation/run-now`);
      toast.dismiss(loadingToast);
      
      // Show detailed success message
      const stats = response.data.analysis;
      if (stats) {
        toast.success(`Data generated successfully!\n${stats.transactions_generated || 0} transactions, ${stats.investments_generated || 0} investments`);
      } else {
        toast.success('Data generated successfully!');
      }
      
      // Refresh users list (suppress errors if this fails)
      try {
        await fetchUsers();
      } catch (refreshError) {
        console.error('Failed to refresh users list:', refreshError);
        // Don't show error to user since generation itself succeeded
      }
    } catch (error) {
      if (loadingToast) {
        toast.dismiss(loadingToast);
      }
      
      // Show specific error message
      const errorMsg = error.response?.data?.detail || error.message || 'Data generation failed';
      toast.error(errorMsg);
      
      console.error('Data generation error:', error);
    }
  };

  const handleToggleDataGen = async (user, enabled) => {
    try {
      // Get current config
      const response = await api.get(`/admin/users/${user.id}/data-generation/config`);
      const config = { ...response.data, enabled };
      
      // Save updated config
      await api.post(`/admin/users/${user.id}/data-generation/configure`, config);
      toast.success(enabled ? 'Auto-generation enabled' : 'Auto-generation disabled');
      fetchUsers(); // Refresh user list
    } catch (error) {
      toast.error('Failed to toggle auto-generation');
    }
  };

  const handleUploadData = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    const confirmed = window.confirm(
      `⚠️ WARNING: This will REPLACE all existing data for ${uploadingUser.first_name} ${uploadingUser.last_name}.\n\n` +
      `File: ${uploadFile.name}\n\n` +
      `Are you sure?`
    );

    if (!confirmed) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Starting upload...');
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('user_id', uploadingUser.id);

      // Start upload and get upload_id
      const response = await api.post('/admin/upload-excel-data', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadId = response.data.upload_id;
      setUploadId(uploadId);

      // Poll for progress
      const pollInterval = setInterval(async () => {
        try {
          const progressResponse = await api.get(`/admin/upload-progress/${uploadId}`);
          const progressData = progressResponse.data;

          setUploadProgress(progressData.progress_percent);
          setUploadStatus(progressData.current_step_description);

          // Check if complete or failed
          if (progressData.status === 'complete') {
            clearInterval(pollInterval);
            setUploadProgress(100);
            setUploadStatus('Upload completed!');
            
            toast.success(
              `✅ Upload successful!\n${progressData.investments_count} investments, ${progressData.transactions_count} transactions`
            );

            // Close modal after a short delay
            setTimeout(() => {
              setShowUploadModal(false);
              setUploadFile(null);
              setUploadingUser(null);
              setUploadProgress(0);
              setUploadStatus('');
              setUploading(false);
              fetchUsers(); // Refresh to show new upload date
            }, 1500);
          } else if (progressData.status === 'failed') {
            clearInterval(pollInterval);
            setUploading(false);
            toast.error(`Upload failed: ${progressData.error || 'Unknown error'}`);
            setUploadProgress(0);
            setUploadStatus('Upload failed');
          }
        } catch (pollError) {
          console.error('Error polling progress:', pollError);
          // Don't stop polling on single error, might be temporary
        }
      }, 1000); // Poll every second

      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (uploading) {
          setUploading(false);
          toast.error('Upload timeout - please check upload history');
        }
      }, 300000);

    } catch (error) {
      setUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const openUploadModal = (user) => {
    setUploadingUser(user);
    setUploadFile(null);
    setShowUploadModal(true);
  };

  const handleToggleActive = async (user) => {
    try {
      if (user.is_active) {
        await api.post(`/admin/users/${user.id}/deactivate`);
        toast.success('User deactivated');
      } else {
        await api.post(`/admin/users/${user.id}/activate`);
        toast.success('User activated');
      }
      fetchUsers();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleViewAsUser = async (user) => {
    try {
      // Request a login token for this specific user from the backend
      const response = await api.post(`/admin/login-as-user/${user.id}`);
      
      const { access_token, user: userData } = response.data;
      
      // Store the token and user data (simulating a real login)
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      toast.success(`Now viewing as ${user.first_name} ${user.last_name}`);
      
      // Redirect to investor dashboard
      navigate('/dashboard');
      
      // Reload to ensure fresh state
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error('Failed to view as user');
      console.error('View as user error:', error);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      password: '',
      phone_number: user.phone_number || '',
      is_admin: user.is_admin || false,
      is_active: user.is_active !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone_number: '',
      is_admin: false,
      is_active: true
    });
  };

  if (loading) {
    return <div className="text-center py-12">Loading users...</div>;
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">User Management</h1>
            <p className="text-slate-600 mt-1">Manage platform users and permissions</p>
          </div>
          <Dialog open={showModal} onOpenChange={(open) => {
            setShowModal(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4" />
                <span>Add User</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">First Name</label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name</label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium mb-1">Password</label>
                  <input
                    type="password"
                    required={!editingUser}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Phone Number</label>
                <input
                  type="text"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_admin"
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({...formData, is_admin: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="is_admin" className="text-sm font-medium">Admin User</label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active" className="text-sm font-medium">Active Account</label>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-900"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>

        {/* Password Reset Modal */}
        <Dialog open={showPasswordReset} onOpenChange={setShowPasswordReset}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password for {resetPasswordUser?.first_name} {resetPasswordUser?.last_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">
                Enter a new password for this user. The user can change this password after logging in.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-1">
                  Password must be at least 6 characters long
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium mb-1">⚠️ Important:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Share this password with the user securely</li>
                  <li>User can change this password after logging in from their profile settings</li>
                  <li>Make sure to note down the password before closing this dialog</li>
                </ul>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={confirmPasswordReset}
                  disabled={!newPassword || newPassword.length < 6}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  Reset Password
                </button>
                <button
                  onClick={() => {
                    setShowPasswordReset(false);
                    setNewPassword('');
                    setResetPasswordUser(null);
                  }}
                  className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Upload Data Modal */}
        <Dialog open={showUploadModal} onOpenChange={(open) => {
          setShowUploadModal(open);
          if (!open) {
            setUploadFile(null);
            setUploadingUser(null);
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Upload Data for {uploadingUser?.first_name} {uploadingUser?.last_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">
                Upload an Excel file to replace all data for this investor.
              </p>
              
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6">
                {uploadFile ? (
                  <div className="text-center space-y-2">
                    <div className="flex items-center justify-center space-x-2 text-green-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">{uploadFile.name}</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <button
                      onClick={() => setUploadFile(null)}
                      className="text-sm text-slate-600 hover:text-slate-900"
                      disabled={uploading}
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => e.target.files && setUploadFile(e.target.files[0])}
                      className="hidden"
                      id="user-file-upload"
                    />
                    <label
                      htmlFor="user-file-upload"
                      className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer"
                    >
                      Choose Excel File
                    </label>
                    <p className="text-xs text-slate-500 mt-2">
                      Accepted: .xlsx, .xls
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                ⚠️ This will permanently replace all existing investments and transactions for this user.
              </div>

              {/* Progress Bar */}
              {uploading && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-600">{uploadStatus}</span>
                    <span className="font-semibold text-blue-600">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  {uploadProgress < 100 && (
                    <div className="flex items-center justify-center space-x-2 text-xs text-slate-500">
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      <span>Processing... Please wait</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadData}
                  disabled={!uploadFile || uploading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading...' : 'Upload & Replace'}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Auto-Gen</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {Array.isArray(users) && users.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((user) => (
              <tr key={user.id} className="hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-slate-900">{user.first_name} {user.last_name}</p>
                    <p className="text-sm text-slate-500">{user.phone_number || 'No phone'}</p>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-slate-900">{user.email}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    user.is_super_admin ? 'bg-purple-100 text-purple-700' :
                    user.is_admin ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {user.is_super_admin ? 'Super Admin' : user.is_admin ? 'Admin' : 'User'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {user.last_upload_date ? (
                    <div className="text-sm">
                      <p className="text-slate-900 font-medium">
                        {new Date(user.last_upload_date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(user.last_upload_date).toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true 
                        })}
                      </p>
                      {user.last_upload_filename && (
                        <p className="text-xs text-blue-600 mt-1 truncate max-w-xs" title={user.last_upload_filename}>
                          📎 {user.last_upload_filename}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        {user.last_upload_investments}inv, {user.last_upload_transactions}txn
                      </p>
                    </div>
                  ) : (
                    <span className="text-sm text-slate-400">No uploads</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {!user.is_super_admin && !user.is_admin ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={user.auto_generate_enabled || false}
                        onChange={(e) => handleToggleDataGen(user, e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        title="Enable auto-generation"
                      />
                      <button
                        onClick={() => handleOpenDataGenConfig(user)}
                        className="p-1 text-slate-600 hover:bg-slate-100 rounded"
                        title="Configure"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRunDataGenNow(user)}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                        title="Run Now"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">N/A</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    {!user.is_super_admin && !user.is_admin && (
                      <button
                        onClick={() => handleViewAsUser(user)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                        title="View as User"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => openEditModal(user)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    {!user.is_super_admin && !user.is_admin && (
                      <button
                        onClick={() => openUploadModal(user)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                        title="Upload Data"
                      >
                        <Upload className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handlePasswordReset(user)}
                      className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                      title="Reset Password"
                    >
                      <Key className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleActive(user)}
                      className={`p-2 rounded-lg ${
                        user.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'
                      }`}
                      title={user.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {user.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                    </button>
                    {!user.is_super_admin && (
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Pagination Controls */}
        {users.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t">
            <div className="text-sm text-slate-600">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, users.length)} of {users.length} users
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-slate-600">
                Page {currentPage} of {Math.ceil(users.length / itemsPerPage)}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(users.length / itemsPerPage), currentPage + 1))}
                disabled={currentPage >= Math.ceil(users.length / itemsPerPage)}
                className="px-3 py-1 border rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 border rounded text-sm"
              >
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
            </div>
          </div>
        )}

        {/* Data Generation Configuration Modal */}
        <Dialog open={showDataGenModal} onOpenChange={setShowDataGenModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Auto Data Generation Settings - {dataGenUser?.first_name} {dataGenUser?.last_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 mt-4">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Enable Auto-Generation</p>
                  <p className="text-sm text-slate-600">Automatically generate fresh data on schedule</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dataGenConfig.enabled}
                    onChange={(e) => setDataGenConfig({...dataGenConfig, enabled: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Schedule Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Schedule Type</label>
                <select
                  value={dataGenConfig.schedule_type}
                  onChange={(e) => setDataGenConfig({...dataGenConfig, schedule_type: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>

              {/* Schedule Time */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Run Time</label>
                <input
                  type="time"
                  value={dataGenConfig.schedule_time}
                  onChange={(e) => setDataGenConfig({...dataGenConfig, schedule_time: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">Server time (UTC)</p>
              </div>

              {/* Schedule Day (for weekly) */}
              {dataGenConfig.schedule_type === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Day of Week</label>
                  <select
                    value={dataGenConfig.schedule_day}
                    onChange={(e) => setDataGenConfig({...dataGenConfig, schedule_day: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>
              )}

              {/* Historical Patterns Analysis */}
              {historicalPatterns ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 font-medium mb-3">📊 Detected Historical Patterns:</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded p-3">
                      <p className="text-xs text-slate-600">Avg Transactions/Day</p>
                      <p className="text-lg font-bold text-green-700">{historicalPatterns.avg_txns_per_day}</p>
                      <p className="text-xs text-slate-500">Based on {historicalPatterns.total_historical_txns} txns</p>
                    </div>
                    <div className="bg-white rounded p-3">
                      <p className="text-xs text-slate-600">Avg Investments/Day</p>
                      <p className="text-lg font-bold text-green-700">{historicalPatterns.avg_invs_per_day}</p>
                      <p className="text-xs text-slate-500">Based on {historicalPatterns.total_historical_invs} invs</p>
                    </div>
                    <div className="bg-white rounded p-3">
                      <p className="text-xs text-slate-600">Last Transaction</p>
                      <p className="text-sm font-semibold text-slate-900">{historicalPatterns.latest_txn_date}</p>
                      <p className="text-xs text-slate-500">{historicalPatterns.days_since_last_txn} days ago</p>
                    </div>
                    <div className="bg-white rounded p-3">
                      <p className="text-xs text-slate-600">Data Span</p>
                      <p className="text-lg font-bold text-slate-900">{historicalPatterns.days_span} days</p>
                      <p className="text-xs text-slate-500">Historical period</p>
                    </div>
                  </div>
                  <div className="mt-3 p-2 bg-white rounded">
                    <p className="text-xs text-slate-600 mb-1">Transaction Types:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(historicalPatterns.type_distribution || {}).map(([type, percent]) => (
                        <span key={type} className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                          {type}: {(percent * 100).toFixed(1)}%
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">⚠️ Unable to analyze patterns - insufficient data</p>
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-medium mb-2">🤖 Intelligent Data Generation:</p>
                <ul className="text-xs text-blue-700 space-y-1 ml-4 list-disc">
                  <li>Analyzes historical patterns automatically</li>
                  <li>Backfills from last transaction date to today</li>
                  <li>Varies daily amounts realistically (no fixed numbers)</li>
                  <li>Maintains current ROI and return percentages</li>
                  <li>Uses existing data as templates for consistency</li>
                  <li>Automatically clears cache for fresh calculations</li>
                </ul>
              </div>

              {/* Last Generation Stats */}
              {dataGenConfig.last_generation && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-slate-900 mb-2">📈 Last Generation:</p>
                  <p className="text-xs text-slate-600 mb-2">
                    {new Date(dataGenConfig.last_generation).toLocaleString()}
                  </p>
                  {dataGenConfig.last_generation_stats && (
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-slate-600">Days Filled</p>
                        <p className="text-lg font-bold text-slate-900">{dataGenConfig.last_generation_stats.days_filled || 0}</p>
                      </div>
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-slate-600">Transactions</p>
                        <p className="text-lg font-bold text-blue-600">{dataGenConfig.last_generation_stats.transactions_generated || 0}</p>
                      </div>
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-slate-600">Investments</p>
                        <p className="text-lg font-bold text-green-600">{dataGenConfig.last_generation_stats.investments_generated || 0}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={handleSaveDataGenConfig}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Configuration
                </button>
                <button
                  onClick={() => setShowDataGenModal(false)}
                  className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
